// src/hooks/useChat.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { sanitizeHead, buildIframeDoc } from '../utils/htmlSanitizer.js';
import useCollabSession     from './useCollabSession.js';
import useChatMessages      from './useChatMessages.js';
import useIframeHistory     from './useCanvasHistory.js';
import useSendMessage       from './useSendMessage.js';
import useChatUI            from './useChatUI.js';
import useParentHistory     from './useParentHistory.js';
import usePreferences       from '../contexts/PreferencesContext.js';
import useScrollToBottom    from './useScrollToBottom.js';
import usePrismHighlighting from './usePrismHighlighting.js';
import useThumbnailUploader from './useThumbnailUploader.js';

export default function useChat(isOwner) {
  // ─── routing / id ref ──────────────────────────────────────────────────────
  const { username: ownerName, chatId } = useParams();
  const navigate = useNavigate();
  const chatIdRef = useRef(chatId);
  useEffect(() => { chatIdRef.current = chatId; }, [chatId]);

  // ─── preferences & UI ──────────────────────────────────────────────────────
  const prefs = usePreferences();
  const ui = useChatUI(isOwner);
  const {
    input, setInput,
    uploadedImage, setUploadedImage,
    viewMode, setViewMode, toggleView,
    commentInput, setCommentInput,
    comments, onCommentSubmit,
  } = ui;

  // ─── refs for scrolling & iframe ───────────────────────────────────────────
  const chatEndRef = useRef(null);
  const iframeRef  = useRef(null);

  // ─── canvas content & code history ─────────────────────────────────────────
  const [canvasContent, setCanvasContent] = useState('');
  const [lastHtml, setLastHtml]           = useState('');
  const [lastCss, setLastCss]             = useState('');
  const [lastJs, setLastJs]               = useState('');

  // ─── collaboration & canvas sync ──────────────────────────────────────────
  const {
    takeControl,
    controllerId,
    isController,
    collabSessionActive,
    setCollabSessionActive,
    collabSessionElapsed,
    sessionRoom,
    socket,
  } = useCollabSession(canvasContent, setCanvasContent);

  // ─── code/iframe history ────────────────────────────────────────────────────
  const {
    history,
    timestamps,
    historyIndex,
    jumpToVersion,
    appendVersion,
  } = useIframeHistory({
    chatId,
    canvasContent,
    setCanvasContent,
    lastHtml, setLastHtml,
    lastCss,  setLastCss,
    lastJs,   setLastJs,
    collabSessionActive,
    isController,
    sessionRoom,
    socket,
    ownerName,
  });

  // ─── parent chat history ───────────────────────────────────────────────────
  const parent = useParentHistory(chatId);
  const {
    showParentHistory,
    setShowParentHistory,
    parentMessages,
    loading: loadingParentHistory,
    loadParentHistory,
    parentChatId,
    parentUsername,
  } = parent;

  // ─── messages load & persistence ───────────────────────────────────────────
  const {
    messages,
    setMessages,
    loading: loadingMessages,
    persistMessage,
  } = useChatMessages(chatId);

  /** Keeps the latest `messages` in a ref so other callbacks can
      synchronously read it without waiting for React state */
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const [ optionState, setOptionState ] = useState(
    /** @type { {
          optionSetId: number,
          status: 'pending' | 'accepted',
          options: Array<{
            id: number,
            idx: number,
            html: string,
            css: string,
            js: string,
            raw_text: string
          }>,
          hoveredId: number|null,
          acceptedId: number|null
        } | null } */
    null
  );

  // Hover an option to live‐preview
  const setHoveredId = (id) => {
    setOptionState((s) => s ? { ...s, hoveredId: id } : s);
  };

  // Accept an option to promote to Version 1
  const setAcceptedId = (id) => {
    setOptionState((s) => s ? { ...s, status: 'accepted', acceptedId: id } : s);
  };

  // ─── hydrate: pull “latest pending OptionSet” on first load ───────────────
  useEffect(() => {
    if (!chatId || loadingMessages) return;   // wait until history is in state

    let cancelled = false;

    const hydratePendingOptions = async () => {
      try {
        // 1) try pending
        let resp = await fetch(`/api/chats/${chatId}/pending-option-set`);
        if (resp.status !== 204) {                // <-- we **did** get a pending set
          const { option_set_id, options, created_at } = await resp.json();

          // prime state
          setOptionState({
            optionSetId : option_set_id,
            status      : 'pending',
            options,
            hoveredId   : options[0]?.id ?? null,
            acceptedId  : null
          });

          // inject bubble (buttons enabled)
          setMessages(m => {
            const already = m.some(
              msg => msg.metadata?.isOptionBatch &&
                    msg.metadata.optionSetId === option_set_id
            );
            if (already) return m;

            const bubble = {
              key      : `ai-pending-${option_set_id}`,
              sender   : 'ai',
              text     : null,
              timestamp: created_at,
              metadata : {
                isOptionBatch: true,
                optionSetId: option_set_id,
                options
              }
            };

            // ▸ place the bubble where it belongs chronologically
            const idx = m.findIndex(
              msg => new Date(msg.timestamp) > new Date(created_at)
            );
            if (idx === -1) return [...m, bubble];              // newest → append
            return [...m.slice(0, idx), bubble, ...m.slice(idx)];
          });

          // preview Option A right away
          if (options[0]?.id) {
            setHoveredId(options[0].id);
            jumpToOption(0);
          }
          return;                                  // done
        }

        // 2) nothing pending → try ACCEPTED
        if (resp.status === 204) {
          // 2) nothing pending → try accepted
          resp = await fetch(`/api/chats/${chatId}/accepted-option-set`);
          if (resp.status === 204 || cancelled) return;   // nothing at all
          const { option_set_id, options, accepted_option_id, created_at } =
            await resp.json();

        const acceptedIdx = options.findIndex(o => o.id === accepted_option_id);

        // build accepted state
        setOptionState({
          optionSetId : option_set_id,
          status      : 'accepted',
          options,
          hoveredId   : accepted_option_id,
          acceptedId  : accepted_option_id
        });

        // inject disabled bubble
        setMessages(m => {
          const already = m.some(
            msg => msg.metadata?.isOptionBatch &&
                  msg.metadata.optionSetId === option_set_id
          );
          if (already) return m;

          const bubble = {
            key      : `ai-accepted-${option_set_id}`,
            sender   : 'ai',
            text     : null,
            timestamp: created_at,
            metadata : {
              isOptionBatch: true,
              optionSetId: option_set_id,
              options
            }
          };

          const idx = m.findIndex(
            msg => new Date(msg.timestamp) > new Date(created_at)
          );
          if (idx === -1) return [...m, bubble];
          return [...m.slice(0, idx), bubble, ...m.slice(idx)];
        });

        // show the accepted variant in the canvas
        if (acceptedIdx >= 0) jumpToOption(acceptedIdx);
          return;
      }
      // 3) immediately preview Option A
      // if (options[0]?.id) setHoveredId(options[0].id);
      } catch (e) {
        console.error('hydratePendingOptions:', e);
      }
    };

    hydratePendingOptions();
    return () => { cancelled = true; };
  }, [chatId, loadingMessages]);    // run once per chat open

  // ─── OPTIONS: draft‐batch state ─────────────────────────────────────────────

 const optionRef = useRef(optionState);        // ❶
 useEffect(() => { optionRef.current = optionState; },
            [optionState]);                    // ❷ keeps ref fresh
  
  // ───[ NEW ]──────────────────────────────────────────────────────────────────
  //
  // If the user hovers Option #i (i ∈ {0,1,2}), we want to render that blob right
  // away—even though it’s not yet in “history.”  That means building a one‐off
  // iframe document from the raw html/css/js strings.  We’ll call it jumpToOption(i).
  //
  const jumpToOption = useCallback(
    (index) => {
      // ─── DEBUG ──────────────────────────────────────────────────────────────
      const os = optionRef.current;

      console.log("[jumpToOption] called with index:", index);
      console.log("[jumpToOption] current optionState:", optionState);
      if (!os || !os.options) {
      // if (!optionState || !optionState.options) {
        console.warn("[jumpToOption] no optionState or empty options array");
        return;
      }
      // Simply grab the Nth element of the array (0 → first option, 1 → second, etc.)
      // const opt = optionState.options[index];
      const opt = os.options[index];
      if (!opt) {
        console.warn(`[jumpToOption] no option at index ${index}`);
        return;
      }

      // Build a self‐contained HTML doc from the parsed html/css/js:
      const doc = buildIframeDoc({
        cleanHead: sanitizeHead(""),
        cssCode:   opt.css,
        bodyInner: opt.html,
        jsCode:    opt.js,
      });

      setCanvasContent(doc);
      setLastHtml(opt.html);
      setLastCss(opt.css);
      setLastJs(opt.js);

      setHoveredId(opt.id);
    },
    [optionState]
  );

  // ───[ NEW ]──────────────────────────────────────────────────────────────────
  //
  // Once the user “Accepts” option #i, we want to copy that same bundle into a real
  // Version (so that jumpToVersion(i) works from then on).  Fortunately, your existing
  // appendVersion(...) already does exactly what we need (persist + update history).
  // We simply wrap it here.
  //
  const appendOptionAsVersion = useCallback(
    async (optionIdx) => {
      if (!optionState || !optionState.options) return;
      const opt = optionState.options.find(o => o.idx === optionIdx);
      if (!opt) return;

      // 1) optimistically mark this as “accepted” locally
      setAcceptedId(opt.id);

      // 2) persist on the server via appendVersion({ htmlCode, cssCode, jsCode, userMessageId, aiMessageId }):
      //    Note: in this first‐prompt scenario, you probably already know userMessageId and aiMessageId.
      //    If you don’t, simply pass null or leave them out; your appendVersion will skip thumbnail, etc.
      await appendVersion({
        htmlCode: opt.html,
        cssCode:  opt.css,
        jsCode:   opt.js,
        persist: false
      });

      // 3) inform the backend so it can broadcast “version_change” over WebSocket
      try {
        // Within the backend when calling /accept:
        // Simultaneously with marking the option as accepted,
        // The option gets copied as the first version for the chat
        await fetch(
          `/api/option_sets/${optionState.optionSetId}/accept`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ option_id: opt.id })
          }
        );
      } catch (err) {
        console.error('Failed to tell server about accepted Option:', err);
        setAcceptedId(null);
      }
      // At this point, your useCanvasHistory listener will get 'version_change'
      // and ensure that the new Version really appears in the dropdown.
    },
    [appendVersion, optionState]
  );
  // ──────────────────────────────────────────────────────────────────────────

  // ─── sendMessage logic (including first‐prompt => n=3) ─────────────────────
  const { sendMessage, loading: sending } = useSendMessage({
    chatIdRef,
    persistMessage,
    setMessages,
    appendVersion,
    lastHtml,
    lastCss,
    lastJs,
    historyIndex,
    timestamps,
    iframeRef,
    setInput,
    setUploadedImage,
    navigate,
    socket,
    setOptionState,
    setHoveredId,
    jumpToOption,
    setCanvasContent,
    setLastHtml,
    setLastCss,
    setLastJs
  });

  // Listen for `option_set_created` and `option_accepted` over WebSocket
  useEffect(() => {
    if (!socket || !chatId) return;

    // Fired right after our first prompt (n=3) persists on server
    const onOptionSetCreated = ({ optionSetId, options }) => {
      console.log("➤ raw options from server:", options);
      const exists = messagesRef.current.some(
        m => m.metadata?.isOptionBatch && m.metadata.optionSetId === optionSetId
      );
      if (exists) return;
      setOptionState({
        optionSetId,
        status: 'pending',
        options,
        hoveredId: null,
        acceptedId: null
      });
    };

    // Fired when someone clicks “Accept” and server creates Version 1
    const onOptionAccepted = ({ optionSetId, acceptedOptionId }) => {
      setOptionState((s) => {
        if (!s || s.optionSetId !== optionSetId) return s;
        return {
          ...s,
          status: 'accepted',
          acceptedId: acceptedOptionId
        };
      });
    };

    socket.on('option_set_created', onOptionSetCreated);
    socket.on('option_accepted',   onOptionAccepted);

    return () => {
      socket.off('option_set_created', onOptionSetCreated);
      socket.off('option_accepted',   onOptionAccepted);
    };
  }, [socket, chatId]);

  // ─── side‐effects: scroll, syntax highlight, thumbnail ────────────────────
  useScrollToBottom(chatEndRef, [messages, uploadedImage]);
  usePrismHighlighting(viewMode, canvasContent);
  const { onIframeLoad } = useThumbnailUploader(chatId, iframeRef, sending);

  /* true while the very first Option–batch exists but hasn’t been accepted */
  const awaitingOption = optionState?.status === 'pending';

  // ─── expose everything ─────────────────────────────────────────────────────
  return {
    // UI & messages
    uploadedImage,
    setUploadedImage,
    messages,
    loadingMessages,
    input,
    setInput,
    sendMessage,

    // scrolling & view toggle
    chatEndRef,
    viewMode,
    setViewMode,
    toggleView,

    // code/iframe history
    history,
    timestamps,
    historyIndex,
    jumpToVersion,
    lastHtml,
    lastCss,
    lastJs,
    canvasContent,
    loading: sending,

    // iframe thumbnail
    iframeRef,
    onIframeLoad,

    // collaboration
    isController,
    takeControl,
    controllerId,
    collabSessionActive,
    setCollabSessionActive,
    collabSessionElapsed,

    // parent history
    showParentHistory,
    setShowParentHistory,
    parentMessages,
    loadingParentHistory,
    loadParentHistory,
    parentChatId,
    parentUsername,

    // comments
    comments,
    commentInput,
    setCommentInput,
    onCommentSubmit,

    // ─── OPTIONS: draft batch state & setters ───────────────────────────────
    optionState,
    setHoveredId,
    setAcceptedId,
    jumpToOption,
    appendOptionAsVersion,
    awaitingOption
  };
}
